import React, { ChangeEvent, Ref, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { CSSProperties } from '@emotion/serialize';
import { FieldError } from 'react-hook-form';
import { switchProp } from 'styled-tools';
import { useInputField } from '$hooks';
import { StyledHelpText, StyledInvalidMessage, StyledSelect } from '../input-field/style';
import { SvgIcon } from '../svg-icon';

type InputProps = React.InputHTMLAttributes<HTMLSelectElement>;

type SelectProps = {
    label?: string;
    helpText?: string;
    error?: FieldError;
    spacingBottom?: 'extrasmall' | 'small' | 'medium' | 'large';
    disabled?: boolean;
    onChangeSideEffect?: (event: ChangeEvent<HTMLSelectElement>) => void;
    appStyle?: boolean;
    style?: CSSProperties;
} & Omit<InputProps, 'placeholder'>;

export const Select = React.forwardRef<HTMLSelectElement, React.PropsWithChildren<SelectProps>>(
    (
        {
            label,
            id,
            helpText,
            children,
            error,
            disabled,
            spacingBottom,
            onChange,
            onChangeSideEffect,
            appStyle,
            style,
            ...rest
        }: React.PropsWithChildren<SelectProps>,
        ref: Ref<HTMLSelectElement>
    ) => {
        const [hasValue, setHasValue] = useState(!!(rest.value || rest.defaultValue));
        const { fieldId, helpTextId, describedById, showHelpText, invalidMessageId } = useInputField({
            id,
            helpText,
        });

        const handleOnChange = (event: ChangeEvent<HTMLSelectElement>) => {
            setHasValue(!!event.target.value);
            onChange?.(event);
            onChangeSideEffect?.(event);
        };

        useEffect(() => {
            setHasValue(!!rest.value);
        }, [rest.value]);

        const isInvalid = !!error;
        const isValid = !error;

        return (
            <SelectWrapper key={fieldId} spacingBottom={spacingBottom} style={style}>
                <StyledSelect
                    isInvalid={isInvalid}
                    isValid={isValid}
                    hasValue={hasValue}
                    ref={ref}
                    aria-describedby={describedById}
                    disabled={disabled}
                    onChange={handleOnChange}
                    appStyle={appStyle}
                    {...rest}
                >
                    {!!label && (
                        <option disabled selected hidden>
                            {label}
                        </option>
                    )}
                    {children}
                </StyledSelect>
                <div>
                    {error && <StyledInvalidMessage id={invalidMessageId}>{error.message}</StyledInvalidMessage>}
                    {showHelpText && <StyledHelpText id={helpTextId}>{helpText}</StyledHelpText>}
                </div>
                <SelectIconWrapper>
                    <SelectIcon svg="chevronDown" />
                </SelectIconWrapper>
            </SelectWrapper>
        );
    }
);

type SelectWrapperProps = {
    spacingBottom?: 'extrasmall' | 'small' | 'medium' | 'large';
};

const SelectWrapper = styled.div<SelectWrapperProps>(
    {
        position: 'relative',
        cursor: 'pointer',
    },
    switchProp('spacingBottom', {
        extrasmall: ({ theme: { space } }) => ({
            marginBottom: space[3],
        }),
        small: ({ theme: { space } }) => ({
            marginBottom: space[5],
        }),
        medium: ({ theme: { space } }) => ({
            marginBottom: space[7],
        }),
        large: ({ theme: { space } }) => ({
            marginBottom: space[9],
        }),
    })
);

const SelectIconWrapper = styled.div(({ theme }) => ({
    height: '40px',
    width: '40px',
    borderLeft: `1px solid ${theme.colors.primaryGreyLight}`,
    position: 'absolute',
    right: '0px',
    top: '5px',
    pointerEvents: 'none',
}));

const SelectIcon = styled(SvgIcon)(() => ({
    height: '30px',
    width: '30px',
    paddingTop: '12px',
    paddingLeft: '7px',
    pointerEvents: 'none',
}));
